/**
 * Opengater backend service registry.
 *
 * Unlike the Backup Manager (single `/api/v1` proxied through our own
 * FastAPI gateway) and the Auth Manager (one upstream — `VITE_AUTH_BACKEND_URL`),
 * the Opengater Manager talks **directly from the browser** to FIVE separate
 * FastAPI microservices, each on its own origin. This is the direct-from-FE
 * pattern (same as Auth Manager) — no backend proxy — so every service must
 * CORS-allow the panel's origin.
 *
 * Base URLs are ORIGIN-ONLY (scheme://host:port). Callers pass the full path
 * including each service's own prefix, because the prefixes are NOT uniform:
 *   - auth (GlobalAuth)  → `/auth/...`, `/admin/...`, `/users/me`   (no /api/v1)
 *   - users              → `/api/v1/admin/...`
 *   - locations          → `/api/v1/admin/...`
 *   - news               → `/admin/...`                            (no /api/v1)
 *   - payments           → `/api/v1/admin/...`
 *
 * The auth service is special: it mints the JWT every other service trusts,
 * and it's where refresh happens. See `client.ts` / `auth.ts`.
 */
const SERVICE_META = {
    auth: { label: 'Auth (GlobalAuth)', envVar: 'VITE_OG_AUTH_URL' },
    users: { label: 'Users', envVar: 'VITE_OG_USERS_URL' },
    locations: { label: 'Locations', envVar: 'VITE_OG_LOCATIONS_URL' },
    news: { label: 'News', envVar: 'VITE_OG_NEWS_URL' },
    payments: { label: 'Payments', envVar: 'VITE_OG_PAYMENTS_URL' },
};
/**
 * Resolve a service's origin from env. Returns null when unconfigured so the
 * client can surface a clean "service not configured" error instead of firing
 * a request at `undefined/...`.
 *
 * Trailing slashes are trimmed so callers can always pass a leading-slash path.
 */
export function ogServiceBase(service) {
    const meta = SERVICE_META[service];
    const raw = import.meta.env[meta.envVar];
    if (!raw)
        return null;
    return raw.replace(/\/+$/, '');
}
export function ogServiceLabel(service) {
    return SERVICE_META[service].label;
}
